import React from "react";
import {
  H3,
  Text,
  Card,
  Elevation,
  Button,
  ControlGroup,
  Tag,
  NonIdealState,
  Spinner,
  Divider,
  TextArea,
  FormGroup,
  InputGroup,
} from "@blueprintjs/core";
import { inject, observer } from "mobx-react";
import { apiCallPost, apiCallPatch } from "../../../../micro/fns/util";
import cogoToast from "cogo-toast";
import SingleIDSelectWrapper from "../../../../micro/utils/SingleIDSelectWrapper";
import ReactQuill from "react-quill";
import { buildProcedureDischargeSummaryName } from "../../../utils/procedureSummary";

class DischargeTab extends React.Component {
  state = {
    // Template selection
    loadedTemplateNames: [],
    isLoadingTemplateNames: false,
    selectedTemplate: null,
    loadedTemplate: null,
    isLoadingTemplate: false,

    // Discharge notes
    dischargeDiagnosis: "",
    reasonForAdmission: "",
    investigation: "",
    significantFindings: "",
    dischargeOtNotes: "",
    procedureDischargeSummaryName: "",
    originalDischargeDiagnosis: "",
    originalReasonForAdmission: "",
    originalInvestigation: "",
    originalSignificantFindings: "",
    originalOtNotes: "",
    originalProcedureDischargeSummaryName: "",

    // Save state
    isSaving: false,
    isTemplateSaving: false,
    templateChanged: false,
    isNotesDirty: false,
  };

  // Load template names (skeleton data) - called on component mount
  loadTemplateNames = async () => {
    try {
      this.setState({ isLoadingTemplateNames: true });
      const response = await apiCallPost(
        `/meta-sx/${this.props.authStore.prefix}/sxDischargeTemplateMaster/getAll/skeleton`
      );

      this.setState({
        loadedTemplateNames: response || [],
        isLoadingTemplateNames: false,
      });
    } catch (err) {
      console.error("Error loading template names:", err);
      cogoToast.error("Failed to load discharge templates");
      this.setState({ isLoadingTemplateNames: false });
    }
  };

  // Load full template content by ID
  loadTemplate = async (
    templateId,
    previousTemplateName = "",
    previousTemplateId = "",
    shouldPersist = true
  ) => {
    try {
      this.setState({ isLoadingTemplate: true });
      const response = await apiCallPost(
        `/meta-sx/${this.props.authStore.prefix}/sxDischargeTemplateMaster/getById`,
        {
          _id: templateId,
        }
      );

      this.setState({
        loadedTemplate: response,
        isLoadingTemplate: false,
        templateChanged: false,
      });

      // Only persist and audit when this is a new template selection, not when loading existing template for display
      if (shouldPersist && previousTemplateId !== templateId) {
        try {
          const surgeryId = this.props.surgery?.id;
          const prefix = this.props.authStore?.prefix;
          if (surgeryId && prefix) {
            await apiCallPatch(
              `/v3/${prefix}/neoSxBookingMaster/update/${surgeryId}`,
              {
                selectedTemplateId: templateId,
                selectedTemplateName: response?.entityName || "",
                selectedTemplateContent: response?.content || "",
              }
            );

            const entry = {
              resourceType: "SURGERY_BOOKING",
              resourceId: surgeryId,
              resourceName: `Surgery - ${this.props.surgery?.patientName || "Unknown Patient"
                }`,
              actionType: "UPDATE",
              actionSlug: `Updated discharge template: "${previousTemplateName || "Empty"
                }" → "${response?.entityName || "Unnamed"}"`,
              actionMetadata: {
                fieldName: "selectedTemplateName",
                changeType: "UPDATE",
                previousValue: previousTemplateName || "",
                newValue: response?.entityName || "",
                itemDetails: {
                  previousTemplateId: previousTemplateId || "",
                  selectedTemplateId: templateId,
                },
              },
            };
            await apiCallPost(`/v3/${prefix}/neoSxAuditLog/createBulk`, [entry]);

            if (this.props.onDischargeUpdate) {
              await this.props.onDischargeUpdate({
                selectedTemplateId: templateId,
                selectedTemplateName: response?.entityName || "",
                selectedTemplateContent: response?.content || "",
              });
            }
          }
        } catch (contentErr) {
          console.error("Persist/audit failed after loading template:", contentErr);
        }
      }
    } catch (err) {
      console.error("Error loading template:", err);
      cogoToast.error("Failed to load template content");
      this.setState({ isLoadingTemplate: false });
    }
  };

  // Handle template selection
  handleTemplateSelection = (template) => {
    const previousTemplateName = this.state.selectedTemplate?.entityName || "";
    const previousTemplateId = this.state.selectedTemplate?._id || "";
    this.setState(
      {
        selectedTemplate: template,
        loadedTemplate: null,
      },
      async () => {
        if (template && template._id) {
          this.loadTemplate(template._id, previousTemplateName, previousTemplateId);
        }
      }
    );
  };

  // Clear template selection
  clearTemplateSelection = () => {
    this.setState({
      selectedTemplate: null,
      loadedTemplate: null,
    });
  };

  // Component mount - load templates immediately
  componentDidMount() {
    this.loadTemplateNames();
    // Initialize discharge notes from props if available
    const dischargeDiagnosis = this.props.surgery?.dischargeDiagnosis || "";
    const reasonForAdmission = this.props.surgery?.reasonForAdmission || "";
    const investigation = this.props.surgery?.investigation || "";
    const significantFindings = this.props.surgery?.significantFindings || "";
    const dischargeOtNotes = this.props.surgery?.dischargeOtNotes || "";
    const procedureDischargeSummaryName =
      this.props.surgery?.procedureDischargeSummaryName || "";
    this.setState({
      dischargeDiagnosis,
      reasonForAdmission,
      investigation,
      significantFindings,
      dischargeOtNotes,
      procedureDischargeSummaryName,
      originalDischargeDiagnosis: dischargeDiagnosis,
      originalReasonForAdmission: reasonForAdmission,
      originalInvestigation: investigation,
      originalSignificantFindings: significantFindings,
      originalOtNotes: dischargeOtNotes,
      originalProcedureDischargeSummaryName: procedureDischargeSummaryName,
      isNotesDirty: false,
    });

    // If a template is already selected in booking, load it
    const preSelectedTemplateId = this.props.surgery?.selectedTemplateId;
    const preSelectedTemplateName =
      this.props.surgery?.selectedTemplateName || "";
    const preSelectedTemplateContent =
      this.props.surgery?.selectedTemplateContent || "";
    if (preSelectedTemplateId) {
      // If we already have the content, just set it without making API calls
      if (preSelectedTemplateContent) {
        this.setState({
          selectedTemplate: {
            _id: preSelectedTemplateId,
            entityName: preSelectedTemplateName,
          },
          loadedTemplate: {
            _id: preSelectedTemplateId,
            entityName: preSelectedTemplateName,
            content: preSelectedTemplateContent,
          },
          templateChanged: false,
        });
      } else {
        // Only fetch if we don't have content, and don't persist (shouldPersist = false)
        this.setState(
          {
            selectedTemplate: {
              _id: preSelectedTemplateId,
              entityName: preSelectedTemplateName,
            },
            loadedTemplate: null,
            templateChanged: false,
          },
          () => {
            this.loadTemplate(
              preSelectedTemplateId,
              preSelectedTemplateName,
              preSelectedTemplateId,
              false
            );
          }
        );
      }
    }

    this.ensureProcedureSummaryPersisted();
  }

  componentDidUpdate(prevProps) {
    // Sync discharge notes from props when data changes and user hasn't edited
    if (
      prevProps.surgery !== this.props.surgery &&
      this.state.isNotesDirty === false
    ) {
      const dischargeDiagnosis = this.props.surgery?.dischargeDiagnosis || "";
      const reasonForAdmission = this.props.surgery?.reasonForAdmission || "";
      const investigation = this.props.surgery?.investigation || "";
      const significantFindings = this.props.surgery?.significantFindings || "";
      const dischargeOtNotes = this.props.surgery?.dischargeOtNotes || "";
      const procedureDischargeSummaryName =
        this.props.surgery?.procedureDischargeSummaryName || "";
      this.setState({
        dischargeDiagnosis,
        reasonForAdmission,
        investigation,
        significantFindings,
        dischargeOtNotes,
        procedureDischargeSummaryName,
        originalDischargeDiagnosis: dischargeDiagnosis,
        originalReasonForAdmission: reasonForAdmission,
        originalInvestigation: investigation,
        originalSignificantFindings: significantFindings,
        originalOtNotes: dischargeOtNotes,
        originalProcedureDischargeSummaryName: procedureDischargeSummaryName,
        isNotesDirty: false,
      });
    }

    // On booking reload, auto-select and load the template if present
    if (
      prevProps.surgery?.selectedTemplateId !==
      this.props.surgery?.selectedTemplateId &&
      this.props.surgery?.selectedTemplateId
    ) {
      const preSelectedTemplateId = this.props.surgery?.selectedTemplateId;
      const preSelectedTemplateName =
        this.props.surgery?.selectedTemplateName || "";
      const preSelectedTemplateContent =
        this.props.surgery?.selectedTemplateContent || "";

      // If we already have the content, just set it without making API calls
      if (preSelectedTemplateContent) {
        this.setState({
          selectedTemplate: {
            _id: preSelectedTemplateId,
            entityName: preSelectedTemplateName,
          },
          loadedTemplate: {
            _id: preSelectedTemplateId,
            entityName: preSelectedTemplateName,
            content: preSelectedTemplateContent,
          },
          templateChanged: false,
        });
      } else {
        // Only fetch if we don't have content, and don't persist (shouldPersist = false)
        this.setState(
          {
            selectedTemplate: {
              _id: preSelectedTemplateId,
              entityName: preSelectedTemplateName,
            },
            loadedTemplate: null,
            templateChanged: false,
          },
          () => {
            this.loadTemplate(
              preSelectedTemplateId,
              preSelectedTemplateName,
              preSelectedTemplateId,
              false
            );
          }
        );
      }
    }

    if (
      prevProps.surgery?.procedureDischargeSummaryName !==
      this.props.surgery?.procedureDischargeSummaryName
    ) {
      const incoming = this.props.surgery?.procedureDischargeSummaryName || "";
      if (incoming && incoming !== this.state.procedureDischargeSummaryName) {
        this.setState({
          procedureDischargeSummaryName: incoming,
          originalProcedureDischargeSummaryName: incoming,
        });
      }
    }

    if (
      prevProps.surgery?.selectedSurgeries !==
      this.props.surgery?.selectedSurgeries ||
      prevProps.surgery?.surgeries !== this.props.surgery?.surgeries
    ) {
      this.ensureProcedureSummaryPersisted();
    }
  }

  ensureProcedureSummaryPersisted = async () => {
    try {
      const surgery = this.props.surgery || {};
      const existing = String(
        this.state.procedureDischargeSummaryName ||
        surgery.procedureDischargeSummaryName ||
        ""
      ).trim();
      if (existing) {
        if (!this.state.procedureDischargeSummaryName) {
          this.setState({
            procedureDischargeSummaryName: existing,
            originalProcedureDischargeSummaryName:
              this.state.originalProcedureDischargeSummaryName || existing,
          });
        }
        return existing;
      }

      const surgeries =
        surgery.selectedSurgeries ||
        surgery.surgeries ||
        [];
      const summary = buildProcedureDischargeSummaryName(surgeries);
      if (!summary) return "";

      const surgeryId = surgery.id;
      const prefix = this.props.authStore?.prefix;
      if (!surgeryId || !prefix) return summary;

      await apiCallPatch(
        `/v3/${prefix}/neoSxBookingMaster/update/${surgeryId}`,
        { procedureDischargeSummaryName: summary }
      );

      try {
        const resourceName = `Surgery - ${this.props.surgery?.patientName || "Unknown Patient"
          }`;
        const display = (v) => (v === null || v === undefined || v === "" ? "Empty" : v);
        const entry = {
          resourceType: "SURGERY_BOOKING",
          resourceId: surgeryId,
          resourceName,
          actionType: "UPDATE",
          actionSlug: `Updated Procedure Performed Display Name: "${display(existing || "")}" → "${display(summary)}"`,
          actionMetadata: {
            fieldName: "procedureDischargeSummaryName",
            changeType: "UPDATE",
            previousValue: existing || "",
            newValue: summary,
            source: "auto",
          },
        };
        await apiCallPost(
          `/v3/${prefix}/neoSxAuditLog/createBulk`,
          [entry]
        );
      } catch (auditErr) {
        console.error("Audit log failed for procedure summary:", auditErr);
      }

      if (this.props.onDischargeUpdate) {
        await this.props.onDischargeUpdate({
          procedureDischargeSummaryName: summary,
        });
      }

      this.setState((prev) => ({
        procedureDischargeSummaryName:
          prev.procedureDischargeSummaryName || summary,
        originalProcedureDischargeSummaryName:
          prev.originalProcedureDischargeSummaryName || summary,
      }));

      return summary;
    } catch (err) {
      console.error("Failed to persist procedure summary:", err);
      return "";
    }
  };

  // Handle text area changes
  handleTextAreaChange = (field, value) => {
    const nextState = { [field]: value };
    this.setState(nextState, () => {
      const isDirty =
        (this.state.dischargeDiagnosis || "") !==
        (this.state.originalDischargeDiagnosis || "") ||
        (this.state.reasonForAdmission || "") !==
        (this.state.originalReasonForAdmission || "") ||
        (this.state.investigation || "") !==
        (this.state.originalInvestigation || "") ||
        (this.state.significantFindings || "") !==
        (this.state.originalSignificantFindings || "") ||
        (this.state.dischargeOtNotes || "") !==
        (this.state.originalOtNotes || "") ||
        (this.state.procedureDischargeSummaryName || "") !==
        (this.state.originalProcedureDischargeSummaryName || "");
      if (this.state.isNotesDirty !== isDirty) {
        this.setState({ isNotesDirty: isDirty });
      }
    });
  };

  // Save discharge notes
  saveDischargeNotes = async () => {
    try {
      if (this.props.canEdit === false) {
        cogoToast.warn("Editing is locked because this surgery has been cancelled");
        return;
      }
      this.setState({ isSaving: true });

      const payload = {
        dischargeDiagnosis: this.state.dischargeDiagnosis || "",
        reasonForAdmission: this.state.reasonForAdmission || "",
        investigation: this.state.investigation || "",
        significantFindings: this.state.significantFindings || "",
        dischargeOtNotes: this.state.dischargeOtNotes || "",
        procedureDischargeSummaryName:
          this.state.procedureDischargeSummaryName || "",
        selectedTemplateId: this.state.selectedTemplate?._id || "",
        selectedTemplateName: this.state.selectedTemplate?.entityName || "",
      };

      // Persist discharge notes to booking
      await apiCallPatch(
        `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.props.surgery?.id}`,
        payload
      );

      // Create audit log entries for text fields that have actually changed
      try {
        const entries = [];
        const resourceId = this.props.surgery?.id;
        const resourceName = `Surgery - ${this.props.surgery?.patientName || "Unknown Patient"
          }`;
        const safe = (v) => (v === null || v === undefined ? "" : v);
        const display = (v) => (v === null || v === undefined || v === "" ? "Empty" : v);

        // Get previous and new values
        const prevDiagnosis = safe(this.state.originalDischargeDiagnosis);
        const newDiagnosis = safe(payload.dischargeDiagnosis);
        const prevReason = safe(this.state.originalReasonForAdmission);
        const newReason = safe(payload.reasonForAdmission);
        const prevInvestigation = safe(this.state.originalInvestigation);
        const newInvestigation = safe(payload.investigation);
        const prevFindings = safe(this.state.originalSignificantFindings);
        const newFindings = safe(payload.significantFindings);
        const prevOt = safe(this.state.originalOtNotes);
        const newOt = safe(payload.dischargeOtNotes);
        const prevProcedureSummary = safe(this.state.originalProcedureDischargeSummaryName);
        const newProcedureSummary = safe(payload.procedureDischargeSummaryName);

        // Only create audit entries for fields that have actually changed
        if (prevDiagnosis !== newDiagnosis) {
          entries.push({
            resourceType: "SURGERY_BOOKING",
            resourceId,
            resourceName,
            actionType: "UPDATE",
            actionSlug: `Updated Discharge Diagnosis: "${display(prevDiagnosis)}" → "${display(newDiagnosis)}"`,
            actionMetadata: {
              fieldName: "dischargeDiagnosis",
              changeType: "UPDATE",
              previousValue: prevDiagnosis,
              newValue: newDiagnosis,
            },
          });
        }

        if (prevReason !== newReason) {
          entries.push({
            resourceType: "SURGERY_BOOKING",
            resourceId,
            resourceName,
            actionType: "UPDATE",
            actionSlug: `Updated Reason for Admission: "${display(prevReason)}" → "${display(newReason)}"`,
            actionMetadata: {
              fieldName: "reasonForAdmission",
              changeType: "UPDATE",
              previousValue: prevReason,
              newValue: newReason,
            },
          });
        }

        if (prevInvestigation !== newInvestigation) {
          entries.push({
            resourceType: "SURGERY_BOOKING",
            resourceId,
            resourceName,
            actionType: "UPDATE",
            actionSlug: `Updated Investigation: "${display(prevInvestigation)}" → "${display(newInvestigation)}"`,
            actionMetadata: {
              fieldName: "investigation",
              changeType: "UPDATE",
              previousValue: prevInvestigation,
              newValue: newInvestigation,
            },
          });
        }

        if (prevFindings !== newFindings) {
          entries.push({
            resourceType: "SURGERY_BOOKING",
            resourceId,
            resourceName,
            actionType: "UPDATE",
            actionSlug: `Updated Significant Findings: "${display(prevFindings)}" → "${display(newFindings)}"`,
            actionMetadata: {
              fieldName: "significantFindings",
              changeType: "UPDATE",
              previousValue: prevFindings,
              newValue: newFindings,
            },
          });
        }

        if (prevOt !== newOt) {
          entries.push({
            resourceType: "SURGERY_BOOKING",
            resourceId,
            resourceName,
            actionType: "UPDATE",
            actionSlug: `Updated OT Notes: "${display(prevOt)}" → "${display(newOt)}"`,
            actionMetadata: {
              fieldName: "dischargeOtNotes",
              changeType: "UPDATE",
              previousValue: prevOt,
              newValue: newOt,
            },
          });
        }

        if (prevProcedureSummary !== newProcedureSummary) {
          entries.push({
            resourceType: "SURGERY_BOOKING",
            resourceId,
            resourceName,
            actionType: "UPDATE",
            actionSlug: `Updated Procedure Performed Display Name: "${display(prevProcedureSummary)}" → "${display(newProcedureSummary)}"`,
            actionMetadata: {
              fieldName: "procedureDischargeSummaryName",
              changeType: "UPDATE",
              previousValue: prevProcedureSummary,
              newValue: newProcedureSummary,
            },
          });
        }

        // Debug logging to help troubleshoot audit issues
        console.log("🔍 DEBUG: Audit logging comparison:", {
          reasonForAdmission: {
            prev: prevReason,
            new: newReason,
            changed: prevReason !== newReason,
          },
          investigation: {
            prev: prevInvestigation,
            new: newInvestigation,
            changed: prevInvestigation !== newInvestigation,
          },
          significantFindings: {
            prev: prevFindings,
            new: newFindings,
            changed: prevFindings !== newFindings,
          },
          otNotes: { prev: prevOt, new: newOt, changed: prevOt !== newOt },
          procedureDischargeSummaryName: {
            prev: prevProcedureSummary,
            new: newProcedureSummary,
            changed: prevProcedureSummary !== newProcedureSummary,
          },
          entriesCreated: entries.length,
        });

        if (entries.length > 0) {
          await apiCallPost(
            `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
            entries
          );
        }
      } catch (auditErr) {
        // Do not block UX on audit errors
        console.error("Audit log failed for discharge notes:", auditErr);
      }

      cogoToast.success("Discharge notes saved successfully");

      // Notify parent component of the updated discharge data
      if (this.props.onDischargeUpdate) {
        await this.props.onDischargeUpdate({
          dischargeDiagnosis: this.state.dischargeDiagnosis || "",
          reasonForAdmission: this.state.reasonForAdmission || "",
          investigation: this.state.investigation || "",
          significantFindings: this.state.significantFindings || "",
          dischargeOtNotes: this.state.dischargeOtNotes || "",
          procedureDischargeSummaryName:
            this.state.procedureDischargeSummaryName || "",
          selectedTemplateId: this.state.selectedTemplate?._id || "",
          selectedTemplateName: this.state.selectedTemplate?.entityName || "",
          selectedTemplateContent: this.state.loadedTemplate?.content || "",
        });
      }

      // After parent updates, reset dirty flag and update originals from current state
      // This will be followed by componentDidUpdate receiving the updated props
      this.setState({
        originalDischargeDiagnosis: this.state.dischargeDiagnosis || "",
        originalReasonForAdmission: this.state.reasonForAdmission || "",
        originalInvestigation: this.state.investigation || "",
        originalSignificantFindings: this.state.significantFindings || "",
        originalOtNotes: this.state.dischargeOtNotes || "",
        originalProcedureDischargeSummaryName:
          this.state.procedureDischargeSummaryName || "",
        isNotesDirty: false,
      });
    } catch (err) {
      console.error("Error saving discharge notes:", err);
      cogoToast.error("Failed to save discharge notes");
    } finally {
      this.setState({ isSaving: false });
    }
  };

  // Handle template edits
  handleTemplateNameChange = (value) => {
    if (!this.state.loadedTemplate) return;
    this.setState({
      loadedTemplate: { ...this.state.loadedTemplate, entityName: value },
      templateChanged: true,
    });
  };

  handleTemplateContentChange = (value) => {
    if (!this.state.loadedTemplate) return;
    this.setState({
      loadedTemplate: { ...this.state.loadedTemplate, content: value },
      templateChanged: true,
    });
  };

  saveTemplateChanges = async () => {
    try {
      if (this.props.canEdit === false) {
        cogoToast.warn("Editing is locked because this surgery has been cancelled");
        return;
      }
      this.setState({ isTemplateSaving: true });
      const surgeryId = this.props.surgery?.id;
      const prefix = this.props.authStore?.prefix;
      if (!surgeryId || !prefix) {
        cogoToast.error("Surgery not loaded");
        return;
      }

      // Persist edited template content to the booking
      await apiCallPatch(`/v3/${prefix}/neoSxBookingMaster/update/${surgeryId}`, {
        selectedTemplateContent: this.state.loadedTemplate?.content || "",
      });

      this.setState({ templateChanged: false });
      cogoToast.success("Template content saved");

      // Notify parent component of the updated template content
      if (this.props.onDischargeUpdate) {
        await this.props.onDischargeUpdate({
          selectedTemplateContent: this.state.loadedTemplate?.content || "",
        });
      }
    } catch (err) {
      console.error("Error saving template content:", err);
      cogoToast.error("Failed to save template content");
    } finally {
      this.setState({ isTemplateSaving: false });
    }
  };

  // Render template selector section
  renderTemplateSelector = () => {
    const isLocked = this.props.canEdit === false;
    return (
      <Card
        elevation={Elevation.TWO}
        style={{ marginBottom: "20px", padding: "20px" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "15px",
          }}
        >
          <div>
            <H3 style={{ margin: 0, color: "var(--text-primary)" }}>
              Discharge Templates
            </H3>
          </div>
          {this.state.selectedTemplate && !isLocked && (
            <Button
              minimal
              icon="cross"
              onClick={this.clearTemplateSelection}
              style={{ color: "var(--text-secondary)" }}
              title="Clear selection"
            />
          )}
        </div>

        <ControlGroup style={{ marginBottom: "15px" }}>
          <Tag large minimal style={{ color: "var(--text-secondary)" }}>
            Search Templates
          </Tag>
          <SingleIDSelectWrapper
            showSelectProp={true}
            width="500px"
            items={this.state.loadedTemplateNames.map((template) => ({
              ...template,
              searchName: template.entityName,
            }))}
            selectedItem={this.state.selectedTemplate}
            labelKey="searchName"
            onChange={this.handleTemplateSelection}
            placeholder="Start typing to search discharge templates..."
            disabled={this.state.isLoadingTemplateNames || isLocked}
          />
        </ControlGroup>

        {this.state.isLoadingTemplateNames && (
          <Text
            style={{
              color: "var(--text-secondary)",
              fontSize: "14px",
              fontStyle: "italic",
            }}
          >
            Loading available templates...
          </Text>
        )}

        {this.state.selectedTemplate && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "var(--bg-surface)",
              borderRadius: "4px",
              border: "1px solid var(--border-color)",
            }}
          >
            <Text
              style={{
                color: "var(--text-primary)",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              Selected Template: {this.state.selectedTemplate.entityName}
            </Text>
          </div>
        )}
      </Card>
    );
  };

  // Render template content
  renderTemplateContent = () => {
    if (!this.state.selectedTemplate) {
      return null;
    }

    if (this.state.isLoadingTemplate) {
      return (
        <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
          <NonIdealState
            icon={<Spinner />}
            title="Loading Template"
            description="Please wait while we load the template content..."
          />
        </Card>
      );
    }

    if (!this.state.loadedTemplate) {
      return (
        <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
          <NonIdealState
            icon="document"
            title="Template Not Found"
            description="The selected template could not be loaded."
            action={
              <Button
                intent="primary"
                onClick={() =>
                  this.loadTemplate(this.state.selectedTemplate._id, "", "", true)
                }
              >
                Retry
              </Button>
            }
          />
        </Card>
      );
    }

    return (
      <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
        <H3
          style={{ margin: 0, marginBottom: "15px", color: "var(--text-primary)" }}
        >
          Edit Discharge Template
        </H3>

        {/* <ControlGroup style={{ marginBottom: "15px" }}>
          <Tag large minimal style={{ color: "var(--text-secondary)" }}>
            Template Name
          </Tag>
          <InputGroup
            fill
            leftIcon="edit"
            value={this.state.loadedTemplate.entityName || ""}
            onChange={(e) => this.handleTemplateNameChange(e.target.value)}
            disabled={this.state.isTemplateSaving}
          />
        </ControlGroup>

        <Divider /> */}

        <div style={{ marginTop: "15px" }}>
          <ReactQuill
            preserveWhitespace={true}
            theme="snow"
            readOnly={this.props.canEdit === false}
            value={this.state.loadedTemplate.content || ""}
            onChange={this.handleTemplateContentChange}
            modules={{
              clipboard: { matchVisual: false },
              toolbar: this.props.canEdit === false ? false : [
                [{ header: [1, 2, false] }],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [
                  { list: "ordered" },
                  { list: "bullet" },
                  { indent: "-1" },
                  { indent: "+1" },
                ],
                ["link", "image"],
                ["clean"],
              ],
            }}
            formats={[
              "header",
              "bold",
              "italic",
              "underline",
              "strike",
              "blockquote",
              "list",
              "bullet",
              "indent",
              "link",
              "image",
            ]}
          />
        </div>

        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: "15px" }}
        >
          {this.state.templateChanged && this.props.canEdit !== false && (
            <Button
              intent="success"
              icon="endorsed"
              onClick={this.saveTemplateChanges}
              loading={this.state.isTemplateSaving}
            >
              Save Template
            </Button>
          )}
        </div>
      </Card>
    );
  };

  // Render discharge notes section
  renderDischargeNotes = () => {
    const isLocked = this.props.canEdit === false;
    const procedureSummary =
      this.state.procedureDischargeSummaryName ||
      this.props.surgery?.procedureDischargeSummaryName ||
      "";
    return (
      <Card
        elevation={Elevation.TWO}
        style={{ padding: "20px", marginTop: "20px" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <H3 style={{ margin: 0, color: "var(--text-primary)" }}>
            Discharge Summary
          </H3>
          {this.state.isNotesDirty && !isLocked && (
            <Button
              intent="primary"
              icon="floppy-disk"
              loading={this.state.isSaving}
              onClick={this.saveDischargeNotes}
            >
              Save Notes
            </Button>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "20px" }}>
          {/* Procedure Summary */}
          <FormGroup
            label="Procedure Performed Display Name"
            labelFor="procedure-discharge-summary"
          >
            <TextArea
              id="procedure-discharge-summary"
              fill
              rows={3}
              disabled={isLocked}
              placeholder="Enter or edit the procedure performed display name..."
              value={procedureSummary}
              onChange={(e) =>
                this.handleTextAreaChange(
                  "procedureDischargeSummaryName",
                  e.target.value
                )
              }
              style={{
                backgroundColor: "var(--bg-surface)",
                color: "var(--text-primary)",
                border: "1px solid var(--border-color)",
              }}
            />
          </FormGroup>

          {/* Diagnosis */}
          <FormGroup label="Diagnosis" labelFor="dischargeDiagnosis">
            <TextArea
              id="dischargeDiagnosis"
              fill
              rows={3}
              disabled={isLocked}
              placeholder="Enter diagnosis..."
              value={this.state.dischargeDiagnosis}
              onChange={(e) =>
                this.handleTextAreaChange("dischargeDiagnosis", e.target.value)
              }
              style={{
                backgroundColor: "var(--bg-surface)",
                color: "var(--text-primary)",
                border: "1px solid var(--border-color)",
              }}
            />
          </FormGroup>

          {/* Reason for Admission */}
          <FormGroup label="Reason for Admission" labelFor="reasonForAdmission">
            <TextArea
              id="reasonForAdmission"
              fill
              rows={3}
              disabled={isLocked}
              placeholder="Enter reason for admission..."
              value={this.state.reasonForAdmission}
              onChange={(e) =>
                this.handleTextAreaChange("reasonForAdmission", e.target.value)
              }
              style={{
                backgroundColor: "var(--bg-surface)",
                color: "var(--text-primary)",
                border: "1px solid var(--border-color)",
              }}
            />
          </FormGroup>

          {/* Investigation */}
          <FormGroup label="Investigation" labelFor="investigation">
            <TextArea
              id="investigation"
              fill
              rows={3}
              disabled={isLocked}
              placeholder="Enter investigations performed..."
              value={this.state.investigation}
              onChange={(e) =>
                this.handleTextAreaChange("investigation", e.target.value)
              }
              style={{
                backgroundColor: "var(--bg-surface)",
                color: "var(--text-primary)",
                border: "1px solid var(--border-color)",
              }}
            />
          </FormGroup>

          {/* Significant Findings */}
          <FormGroup label="Significant Findings" labelFor="significantFindings">
            <TextArea
              id="significantFindings"
              fill
              rows={3}
              disabled={isLocked}
              placeholder="Enter significant findings..."
              value={this.state.significantFindings}
              onChange={(e) =>
                this.handleTextAreaChange("significantFindings", e.target.value)
              }
              style={{
                backgroundColor: "var(--bg-surface)",
                color: "var(--text-primary)",
                border: "1px solid var(--border-color)",
              }}
            />
          </FormGroup>

          {/* OT Notes */}
          <FormGroup
            label="OT Notes"
            labelFor="ot-notes"
            labelInfo="(Operation Theater Notes)"
          >
            <TextArea
              id="ot-notes"
              fill
              rows={5}
              disabled={isLocked}
              placeholder="Enter operation theater notes, surgical details, complications, etc..."
              value={this.state.dischargeOtNotes}
              onChange={(e) =>
                this.handleTextAreaChange("dischargeOtNotes", e.target.value)
              }
              style={{
                backgroundColor: "var(--bg-surface)",
                color: "var(--text-primary)",
                border: "1px solid var(--border-color)",
              }}
            />
          </FormGroup>
        </div>
      </Card>
    );
  };

  render() {
    const { surgery } = this.props;
    const isLocked = this.props.canEdit === false;

    return (
      <div style={{ padding: "20px", paddingTop: "0px" }}>
        {isLocked && (
          <Card
            elevation={Elevation.TWO}
            style={{
              backgroundColor: "#5c255c",
              marginBottom: "16px",
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Tag intent="warning" icon="lock" large minimal>
              Locked
            </Tag>
            <Text style={{ color: "#f5f8fa" }}>
              Discharge fields are locked because this surgery has been cancelled.
            </Text>
          </Card>
        )}

        {/* Template Selector Section */}
        {this.renderTemplateSelector()}

        {/* Template Content Section */}
        {this.renderTemplateContent()}

        {/* Discharge Notes Section */}
        {this.renderDischargeNotes()}
      </div>
    );
  }
}

export default inject("authStore")(observer(DischargeTab));
